import { useFontInjection } from '../../hooks/useFontInjection'
import { useUI } from '../../context/UIContext'
import ProgressBar from '../progress/ProgressBar'
import TierButtons from '../ui/TierButtons'
import DonationFeed from '../feed/DonationFeed'
import DonationModal from '../form/DonationModal'
import { useEffect, useState } from 'react'

function useSlideshowIndex(length: number, intervalMs = 5000) {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (length <= 1) return
    const id = setInterval(() => setIndex(i => (i + 1) % length), intervalMs)
    return () => clearInterval(id)
  }, [length, intervalMs])
  return index
}

const Layout = () => {
  useFontInjection()
  const { openModal } = useUI()

  const desktopSlides: string[] = window.campaignParams?.sidebarImagesDesktop ?? []
  const mobileSlides: string[]  = window.campaignParams?.sidebarImagesMobile  ?? []

  const desktopIndex = useSlideshowIndex(desktopSlides.length)
  const mobileIndex  = useSlideshowIndex(mobileSlides.length)

  return (
    <div className="flex flex-col lg:flex-row min-h-screen" dir="rtl">
      
      {/* ── Right Sidebar ─────────────────────────────────────────── */}
      <aside className="lg:sticky lg:top-0 lg:h-screen lg:flex-shrink-0 relative overflow-hidden p-2">
        {/* Mobile slideshow */}
        {mobileSlides.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt={`Sidebar mobile ${i + 1}`}
            className="block lg:hidden w-full h-auto object-cover absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === mobileIndex ? 1 : 0, position: i === 0 ? 'relative' : 'absolute' }}
          />
        ))}
        {/* Desktop slideshow */}
        {desktopSlides.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt={`Sidebar desktop ${i + 1}`}
            className="hidden lg:block w-full h-full object-cover absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === desktopIndex ? 1 : 0, position: i === 0 ? 'relative' : 'absolute' }}
          />
        ))}
      </aside>

      {/* ── Left Main Content ─────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col">
        
        {/* ── Top Section (Progress & Tiers) ── */}
        <section className="flex-none flex flex-col lg:flex-row p-4">
          {/* Right side of Top Section: Progress Bar */}
          
{/* Right: Story Text (desktop only) */}
          <div
              className="flex-2 text-right leading-relaxed text-sm p-4"
              style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}
            >
              <h1 className="text-base font-bold mb-3 text-[2em] text-red-500">לאה'לע ע"ה לא ביקשה הרבה, היא רק רצתה להיות אמא.</h1>

              <p className="mb-1">במשך 12 שנה היא נתנה את הלב שלה לכל ילד חולה במסדרונות הדסה, כולם הכירו את 'לאה'לע מהמשחקיה', שהיתה דמות אוהבת לכל ילד, בזמן שהיא עצמה מחכה לנס משלה.</p>

              <p className="mb-1">הנסיעה האחרונה לבולגריה הייתה אמורה להיות עוד תחנה בדרך אל הילד שחלמה עליו. היא יצאה מתוך תפילה - ולא חזרה, משאירה אחריה מכתב עם חלום אחד:</p>
              <blockquote className="mb-3 italic border-r-2 border-gray-300 pr-3">"כל כך רציתי להיות אמא, לחבק לאהוב את התינוק שלי, לומר לו 'מאמי פה... מאמי לא עוזבת אותך... רציתי, ריבונו של עולם, כל כך רציתי.. אבל אתה רצית אחרת.."</blockquote>

              <h2 className="text-sm font-semibold text-red-500 text-[1.2em]">החלום שלה לא ייגמר כאן.</h2>
              <p className="mb-1"> אנחנו מקימים לזכרה את "המשחקייה של לאה'לע" - המקום שהיה לה לבית שני. המקום שעליו היא חלמה בימים ובלילות, אנחנו מרחיבים ומשדרגים את הממלכה שלה, כדי שכל ילד חולה ירגיש את החיבוק שהיא כל כך רצתה להעניק.</p>

              <p className="mb-1">הילדים שישחקו שם במשחקיה שלאה'לע כל כך אהבה, הם יהיו הילדים שלה!
              <br/> 
              לאה'לע כבר לא תזכה לחבק ילד משלה, אבל דרכך – החלום שלה יתגשם.
                </p>
                <h2 className="text-xl font-bold text-red-500 text-center text-[1.6em]">בזכותך יהיו לה ילדים!</h2>

              <p
                className="font-bold text-[#E63946] text-base cursor-pointer underline text-center"
                onClick={() => openModal(null)}
              >
                  בחרו את ההנצחה שלכם או הקליקו כאן לתרומה &gt;&gt;&gt;
              </p>
            </div>

          {/* Left side of Top Section: Tiers */}
          <div className="flex-1">
            <TierButtons />
          </div>
        </section>

        {/* ── Bottom Section (Feed) ── */}
        <section className="px-4">
          <div className="flex gap-6 flex-col lg:flex-row">

            {/* Progress Bar — sticky to top */}
            <div className="flex-1 flex flex-col items-center">
              <div className="sticky top-0 w-full bg-white z-10 py-2">
                <ProgressBar />
              </div>
            </div>

            {/* Donation Feed — inner scroll */}
            <div className="flex-1 sticky top-0">
              <DonationFeed />
            </div>

          </div>
        </section>

      </main>

      <DonationModal />

      {/* ── Floating donate button ─────────────────────────────── */}
      {/* {!isModalOpen && (
        <button
          onClick={() => openModal(null)}
          className="fixed bottom-6 left-6 z-50 rounded-full bg-[#E63946] text-white font-bold px-6 py-4 shadow-lg hover:scale-105 active:scale-95 transition-transform cursor-pointer"
          style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif', fontSize: '1.1rem' }}
        >
          לתרומה
        </button>
      )} */}
    </div>
  )
}

export default Layout
